import type { RoadmapAssignee, RoadmapTask, RoadmapTaskInput } from "@/lib/api";

/** ロードマップ (ガントチャート) の日付計算。日付は "YYYY-MM-DD" で持ち、1970-01-01 からの日数 (UTC) で計算する。 */

const MS_PER_DAY = 86_400_000;

export function dayIndex(date: string): number {
	const [y, m, d] = date.split("-").map(Number);
	return Math.round(Date.UTC(y, m - 1, d) / MS_PER_DAY);
}

export function dateOfIndex(index: number): string {
	return new Date(index * MS_PER_DAY).toISOString().slice(0, 10);
}

/** 端末のタイムゾーンでの今日。 */
export function todayIndex(now: Date = new Date()): number {
	return Math.round(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / MS_PER_DAY);
}

/** 0 = 日曜。1970-01-01 は木曜。 */
export function weekdayOf(index: number): number {
	return (((index + 4) % 7) + 7) % 7;
}

export interface DayRange {
	start: number;
	/** この日を含む。 */
	end: number;
}

const PAST_DAYS = 28;
const FUTURE_DAYS = 180;
const TASK_MARGIN_DAYS = 14;

/** 表示する期間。今日の前後と全タスクを余白付きで覆い、月曜から始める。 */
export function timelineRange(
	tasks: readonly { startDate: string; endDate: string }[],
	today: number,
): DayRange {
	let start = today - PAST_DAYS;
	let end = today + FUTURE_DAYS;
	for (const task of tasks) {
		start = Math.min(start, dayIndex(task.startDate) - TASK_MARGIN_DAYS);
		end = Math.max(end, dayIndex(task.endDate) + TASK_MARGIN_DAYS);
	}
	start -= (weekdayOf(start) + 6) % 7;
	return { start, end };
}

/** ドラッグの起点と現在地から、両端を含む期間を作る (逆向きのドラッグも許す)。 */
export function spanBetween(a: number, b: number): DayRange {
	return { start: Math.min(a, b), end: Math.max(a, b) };
}

export type BarDragMode = "move" | "resize-start" | "resize-end";

/** バーのドラッグで日数 delta だけずらした期間。端の伸縮では開始が終了を越えない。 */
export function dragSpan(
	span: DayRange,
	mode: BarDragMode,
	delta: number,
	bounds: DayRange,
): DayRange {
	switch (mode) {
		case "move": {
			const shift = Math.min(Math.max(delta, bounds.start - span.start), bounds.end - span.end);
			return { start: span.start + shift, end: span.end + shift };
		}
		case "resize-start":
			return {
				start: Math.min(Math.max(span.start + delta, bounds.start), span.end),
				end: span.end,
			};
		case "resize-end":
			return {
				start: span.start,
				end: Math.max(Math.min(span.end + delta, bounds.end), span.start),
			};
	}
}

/** 期間内の各月の初日 (期間の初日を含む)。月見出しの位置に使う。 */
export function monthStarts(range: DayRange): { index: number; label: string }[] {
	const result: { index: number; label: string }[] = [];
	for (let i = range.start; i <= range.end; i++) {
		const date = dateOfIndex(i);
		if (date.endsWith("-01")) {
			result.push({
				index: i,
				label: `${Number(date.slice(0, 4))}年${Number(date.slice(5, 7))}月`,
			});
		}
	}
	return result;
}

/** PUT で全項目を置き換えるための、タスクの今の値。 */
export function taskInput(task: RoadmapTask): RoadmapTaskInput {
	return {
		name: task.name,
		description: task.description,
		assigneeUserId: task.assigneeUserId,
		startDate: task.startDate,
		endDate: task.endDate,
		done: task.done,
	};
}

/** 担当者ごとのレーン。key は担当者の ID で、未割り当てのレーンは "" にする。 */
export interface RoadmapLane {
	key: string;
	assignee: RoadmapAssignee | null;
	/** バーの色相。未割り当ては null。 */
	hue: number | null;
}

// 並びの近いレーンほど色相が離れるように並べる。
export const LANE_HUES = [215, 30, 135, 315, 55, 180, 265, 355] as const;

export const UNASSIGNED_LANE_KEY = "";

/**
 * タスクを持つ担当者のレーンを並べ、最後に未割り当てのレーンを置く。未割り当てのレーンは
 * 新しいタスクを作る場所なので、タスクが無くても出す。色相は laneHues (担当者 ID → 色相) で選んだものを優先し、
 * 無ければ表示順で LANE_HUES から割り当てる。
 */
export function buildLanes(
	assignees: readonly RoadmapAssignee[],
	tasks: readonly RoadmapTask[],
	laneHues: ReadonlyMap<string, number> = new Map(),
): RoadmapLane[] {
	const assigned = new Set(tasks.map((task) => task.assigneeUserId));
	return [
		...assignees
			.filter((assignee) => assigned.has(assignee.id))
			.map((assignee, i) => ({
				key: assignee.id,
				assignee,
				hue: laneHues.get(assignee.id) ?? LANE_HUES[i % LANE_HUES.length],
			})),
		{ key: UNASSIGNED_LANE_KEY, assignee: null, hue: null },
	];
}

/** タスクの入るレーン。一覧に無い担当者 (読み込み後に増えたなど) は未割り当てに入れる。 */
export function laneKeyOf(task: RoadmapTask, lanes: readonly RoadmapLane[]): string {
	const key = task.assigneeUserId ?? UNASSIGNED_LANE_KEY;
	return lanes.some((lane) => lane.key === key) ? key : UNASSIGNED_LANE_KEY;
}

export function assigneeIdOfLane(key: string): string | null {
	return key === UNASSIGNED_LANE_KEY ? null : key;
}

/** レーン内で期間の重なるタスクを別の行に積む。開始の早い順に、空いている最初の行へ入れる。 */
export function packRows(tasks: readonly RoadmapTask[]): {
	rowOf: Map<string, number>;
	rowCount: number;
} {
	const sorted = [...tasks].sort(
		(a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate),
	);
	const rowEnds: number[] = [];
	const rowOf = new Map<string, number>();
	for (const task of sorted) {
		const start = dayIndex(task.startDate);
		let row = rowEnds.findIndex((end) => end < start);
		if (row === -1) row = rowEnds.push(0) - 1;
		rowEnds[row] = dayIndex(task.endDate);
		rowOf.set(task.id, row);
	}
	return { rowOf, rowCount: Math.max(rowEnds.length, 1) };
}
